import React, { useMemo } from 'react';

import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';

import { setActiveAdvancementsTab } from '@modules/advancement/pendingItems';
import { AdvancementsTabsNames } from '@modules/advancement/pendingItems/constants';
import { updateAdvSummary } from '@modules/youthProfile/duck/actions';
import { updatingYouthAdvancementSel } from '@modules/youthProfile/duck/selectors';
import { Card, Row, Spin } from '@shared/components';
import { advancementTabNames, unitTypeIdForUnitType } from '@shared/constants';
import { navigateToRosterTab } from '@shared/duck/actions';
import {
  hasNewPermissionSel,
  isMobileSel,
  offlineSel,
  organizationGuidSel,
  selectedOrganizationSel,
} from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';
import { userIdSel } from '@user';

import {
  closeAdvancementReportModal,
  editReportLoadingSel,
  generateAdvancementReportRequest,
  generatingReportSel,
  isEditEnabledSel,
  poidSel,
  reportInfoSel,
  routeEditAdvancementReport,
  setShowPrice,
  showPriceSel,
} from '../../../duck';
import styles from './AdvancementReportForm.less';
import AdvancementReportList from './AdvancementReportList';
import AdvancementReportSummary from './AdvancementReportSummary';
import AdvancementReportTable from './AdvancementReportTable';

// TODO: Review the commented code, is it legacy or for future features?
function AdvancementReportForm(props) {
  const dispatch = useDispatch();
  const {
    loading,
    items,
    onNavigateToEdit,
    isEditEnabled,
    isMobile,
    showPrice,
    poid = null,
    canEditMultipleItems,
    // generating = false,
    onSetShowPrice,
    organizationGuid,
    // onGenerateReportRequest,
    editReportLoading,
  } = props;

  const logedUserId = useSelector(userIdSel);
  const isUpdatingAward = useSelector(updatingYouthAdvancementSel);
  const { unitTypeId } = useSelector(selectedOrganizationSel);

  const orgIsPack = useMemo(
    () => +unitTypeId === unitTypeIdForUnitType.Pack,
    [unitTypeId],
  );

  const onEdit = useMemo(
    () => (isEditEnabled && !poid ? onNavigateToEdit : null),
    [isEditEnabled, poid, onNavigateToEdit],
  );

  const handleToggleShowPrice = () => {
    onSetShowPrice(!showPrice);
  };

  const handleRedirect = () => {
    dispatch(closeAdvancementReportModal());
    dispatch(setActiveAdvancementsTab(AdvancementsTabsNames.TO_PURCHASE));
    dispatch(navigateToRosterTab(advancementTabNames.ADVANCEMENTS));
  };

  const handleAwardAdvancement = item => () => {
    const {
      advancementType,
      advancementId,
      youth: { userId },
    } = item;

    const payload = {
      newAdvancement: {
        id: advancementId,
        awarded: true,
        dateAwarded: DateTime.now().toISODate(),
        dateEarned: DateTime.now().toISODate(),
        awardedUserId: userId,
        leaderApprovedUserId: logedUserId,
        leaderApprovedDate: DateTime.now().toISODate(),
        organizationGuid,
      },
      advancementType,
      advancementId,
      isFromProfile: false,
    };

    dispatch(updateAdvSummary(payload));
  };

  return (
    <Spin size="large" spinning={editReportLoading || isUpdatingAward}>
      <AdvancementReportSummary
        isMobile={isMobile}
        loading={loading}
        items={items}
        showPrice={showPrice}
        canEditMultipleItems={canEditMultipleItems}
        onEdit={onEdit}
        onToggle={handleToggleShowPrice}
      />
      {isMobile ? (
        <AdvancementReportList
          loading={loading}
          items={items}
          onEdit={onEdit}
          showPrice={showPrice}
          onAwardAdvancement={handleAwardAdvancement}
          orgIsPack={orgIsPack}
        />
      ) : (
        <AdvancementReportTable
          loading={loading}
          items={items}
          showPrice={showPrice}
          onAwardAdvancement={handleAwardAdvancement}
          orgIsPack={orgIsPack}
        />
      )}
      <div className={styles.generateInfo}>
        {!poid && (
          <FormattedMessage id="advancementReport.AdvancementReport.extraReportDescription" />
        )}
      </div>
      <div className={styles.instructionsContainer}>
        <Card bordered rounded className={styles.instructions}>
          <Row
            type="flex"
            align="middle"
            justify="center"
            className={styles.note}
            onClick={handleRedirect}
          >
            <FormattedMessage
              id="advancementReportForm.toPurchase.note"
              values={{
                link: (
                  <b>
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault();
                        handleRedirect(poid);
                      }}
                    >
                      <FormattedMessage id="unitAdvancement.tabs.toPurchase" />
                    </a>
                  </b>
                ),
              }}
            />
          </Row>
        </Card>
      </div>
    </Spin>
  );
}

AdvancementReportForm.propTypes = {
  // provided by parent
  items: PropTypes.array.isRequired,
  canEditMultipleItems: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  // provided by component itself
  canEdit: PropTypes.bool.isRequired,
  poid: PropTypes.number,
  generating: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOffline: PropTypes.bool.isRequired,
  editReportLoading: PropTypes.bool.isRequired,
  isEditEnabled: PropTypes.bool.isRequired,
  organizationGuid: PropTypes.string.isRequired,
  reportInfo: PropTypes.object.isRequired,
  showPrice: PropTypes.bool.isRequired,
  onSetShowPrice: PropTypes.func.isRequired,
  onNavigateToEdit: PropTypes.func.isRequired,
  onGenerateReportRequest: PropTypes.func.isRequired,
};

const mapState = state => ({
  isEditEnabled: isEditEnabledSel(state),
  isMobile: isMobileSel(state),
  isOffline: offlineSel(state),
  organizationGuid: organizationGuidSel(state),
  reportInfo: reportInfoSel(state),
  showPrice: showPriceSel(state),
  editReportLoading: editReportLoadingSel(state),
  poid: poidSel(state),
  generating: generatingReportSel(state),
  canEdit: hasNewPermissionSel(state, newPermissions.APPROVE_ADVANCEMENTS),
});

const mapDispatch = dispatch => ({
  onNavigateToEdit: () => dispatch(routeEditAdvancementReport()),
  onSetShowPrice: showPrice => dispatch(setShowPrice(showPrice)),
  onGenerateReportRequest: settings =>
    dispatch(generateAdvancementReportRequest(settings)),
});

export default connect(mapState, mapDispatch)(AdvancementReportForm);
